import type { Teach } from '@pocketprep/types'
import { runCloudFunction } from '@/store/ParseUtils'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { groupModule } from '@/store/group/module'
import { fetchLibLoadable, resetLoadable } from '@/loadingUtils'
import { assignmentModule } from '@/store/assignment/module'

export interface IAssignmentPayload {
    name?: string
    examMetadataId: string
    assignmentId?: string
    assignedOrgStudentIds: string[]
    availableDate?: string
    dueDate?: string
    status: 'draft' | 'active'
    config: ReturnType<Teach.Cloud.upsertAssignment>['attributes']['config']
}

const fetchQuestionDifficultyCountsForExamId = async (examMetadataId: string, forceFetch?: boolean) => {
    await Promise.all([
        organizationModule.actions.fetchCurrentOrganization(),
        examModule.actions.fetchCurrentOrgExams(),
        examModule.actions.fetchExamMetadata(),
        groupModule.actions.fetchCurrentGroup(),
    ])

    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const allExamMetadata = examModule.getters.getExamMetadata()
    const examMetadata = examMetadataId && allExamMetadata?.[examMetadataId]

    if (!currentOrganization || !examMetadata) {
        throw new Error('Unable to get requested exam.')
    }

    return fetchLibLoadable(
        assignmentModule,
        'currentOrgDifficultyCounts',
        currentOrganization.objectId,
        async () =>
            runCloudFunction<Teach.Cloud.fetchQuestionDifficultyCounts>(
                'fetchQuestionDifficultyCounts',
                {
                    examGuid: examMetadata.examGuid,
                    majorVersion: examMetadata.version.split('.')[0] || '',
                    examMetadataId: examMetadata.objectId,
                }
            ),
        forceFetch
    )
}

const upsertAssignment = async (params: IAssignmentPayload) => {
    await Promise.all([
        organizationModule.actions.fetchCurrentOrganization(),
        examModule.actions.fetchCurrentOrgExams(),
        examModule.actions.fetchExamMetadata(),
        groupModule.actions.fetchCurrentGroup(),
    ])

    const {
        name,
        config,
        examMetadataId,
        availableDate,
        dueDate,
        assignedOrgStudentIds,
        assignmentId,
        status,
    } = params

    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const allExamMetadata = examModule.getters.getExamMetadata()
    const examMetadata = allExamMetadata[params.examMetadataId]

    if (!currentOrganization || !examMetadata) {
        throw new Error('Unable to fetch data to create assignment.')
    }


    const newAssignment = await runCloudFunction<Teach.Cloud.upsertAssignment>(
        'upsertAssignment',
        {
            name,
            organizationId: currentOrganization.objectId,
            config,
            examMetadataId,
            assignedOrgStudentIds,
            availableDate,
            assignmentId,
            dueDate,
            status,
        }
    )

    const orgAssignments = await assignmentModule.state.currentOrgAssignments[currentOrganization.objectId]?.value
    if (!orgAssignments) {
        return null
    }
    orgAssignments[newAssignment.objectId] = newAssignment

    return newAssignment
}

const fetchAssignments = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('No current organization found.')
    }

    return fetchLibLoadable(assignmentModule, 'currentOrgAssignments', currentOrganization.objectId, async () =>
        runCloudFunction<Teach.Cloud.fetchAssignments>(
            'fetchAssignments',
            {
                organizationId: currentOrganization.objectId,
            }
        )
    )
}

const cloneAssignment = async (params: { assignmentId: string }) => {
    const assignmentId = params.assignmentId
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()
    if (!assignmentId || !currentOrganization) {
        throw new Error('Missing assignmentId or current organization')
    }
    const newAssignment = await runCloudFunction<Teach.Cloud.cloneAssignment>(
        'cloneAssignment',
        { assignmentId }
    )
    resetLoadable(assignmentModule.state.currentOrgAssignments[currentOrganization.objectId])

    await fetchAssignments()

    return newAssignment
}

const deleteAssignment = async (params: { assignmentId: string }) => {
    const assignmentId = params.assignmentId
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()
    if (!assignmentId || !currentOrganization) {
        throw new Error('Missing assignmentId or current organization')
    }
    const deletedAssignment = await runCloudFunction<Teach.Cloud.deleteAssignment>(
        'deleteAssignment',
        { assignmentId }
    )
    resetLoadable(assignmentModule.state.currentOrgAssignments[currentOrganization.objectId])

    await fetchAssignments()

    return deletedAssignment
}

export default {
    fetchQuestionDifficultyCountsForExamId,
    upsertAssignment,
    fetchAssignments,
    cloneAssignment,
    deleteAssignment,
}
